import React, { useEffect, useState } from "react";
import "./TestInstruction.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ShieldCheck, TimerReset, EyeOff, MousePointerClick, Lock } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BASE_URL, objectivequizApi} from "../../../api/api";

const instructions = [
  {
    icon: <ShieldCheck size={24} />,
    title: "Secure Testing",
    description: "In the test panel, navigating away from the test will result in auto-submission.",
  },
  {
    icon: <TimerReset size={24} />,
    title: "Timed Assessment",
    description: "The test will be timed. You must complete it within the given duration.",
  },
  {
    icon: <EyeOff size={24} />,
    title: "No External Help",
    description: "During the test, you are expected to solve questions without outside assistance.",
  },
  {
    icon: <MousePointerClick size={24} />,
    title: "One-way Navigation",
    description: "You won’t be able to go back to previous questions once you move forward.",
  },
  {
    icon: <Lock size={24} />,
    title: "Browser Lock",
    description: "Test panel will restrict tab-switching, refreshing, or opening new pages.",
  },
];

const ObjectiveTestInstruction = () => {
  const navigate = useNavigate();
  const { type, id } = useParams(); //  Get type and id from URL
  const [testData, setTestData] = useState(null);

  useEffect(() => {
    axios.get(`${BASE_URL}${objectivequizApi}`)
      .then(response => setTestData(response.data))
      .catch(error => {
        console.error("Error fetching test data:", error);
        toast.error("Failed to load test details. Please try again.", {
          position: "top-right",
          autoClose: 3000,
        });
      })
  }, []);

  const handleStartTest = (e) => {
    e.preventDefault();
    const isChecked = document.getElementById("confirm-test").checked;

    if (!isChecked) {
      toast.error("Please click the checkbox to confirm before starting the test.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    toast.success("Test Started! Page Redirecting...", {
      position: "top-right",
      autoClose: 1500,
    });

    setTimeout(() => {
      //  Navigate to the correct test panel based on type and id
      navigate(`/objective-test-panel/${id}`);
    }, 1800);
  };

  return (
    <div className="test-instruction-panel">
      <header className="tip-header"> 
        <p>Read the instructions carefully before starting the test.</p>
      </header>

      <div className="tip-layout">
        <aside className="tip-sidebar">
          <h3>Test Overview</h3>
          {testData ? (
            <ul>
              <li>
                <strong>Test Name:</strong> {testData.test_name}
              </li>
              <li>
                <strong>Subject:</strong> {testData.subject_name || testData.test_subject}
              </li>
              <li>
                <strong>Topic:</strong> {testData.test_topic || "General"}
              </li>
              <li>
                <strong>Description:</strong> {testData.test_description}
              </li>
              <li>
                <strong>Questions:</strong> {type === "objective" ? "Objective" : "Subjective"}
              </li>
              <li>
                <strong>Marking:</strong> {testData.correct_marks} for correct, {testData.incorrect_marks} for incorrect
              </li>
              <li>
                <strong>Attempts Allowed:</strong> {testData.max_attempt}
              </li>
              <li>
                <strong>Start Date:</strong>{" "}
                {new Date(testData.start_date || testData.test_start_date).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </li>
              <li>
                <strong>End Date:</strong>{" "}
                {new Date(testData.end_date || testData.test_end_date).toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </li>
              <li>
                <strong>Navigation:</strong> One-way only
              </li>
              <li>
                <strong>Tab Restrictions:</strong> Applied on test panel
              </li>
            </ul>
          ) : (
            <p>Loading test details...</p>
          )}
        </aside>

        <main className="tip-main">
          <h3>What to Expect</h3>
          <div className="tip-instruction-list">
            {instructions.map((item, idx) => (
              <div className="tip-instruction" key={idx}>
                <div className="tip-icon">{item.icon}</div>
                <div className="tip-text">
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>

      <footer className="tip-footer">
        <form className="tip-footer">
          <div className="tip-disclaimer">
            <div className="tip-checkbox">
              <input type="checkbox" id="confirm-test" required />
              <label htmlFor="confirm-test">
                I understand that once I click start, the test will begin immediately.
              </label>
            </div>
          </div>
          <button className="tip-start-btn" onClick={handleStartTest}>
            Start Test
          </button>
        </form>
      </footer>
    </div>
  );
};

export default ObjectiveTestInstruction;
